import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import type { SavedViewsRef } from '~/client/src/desktop/components/SavedViews'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DrillDownFilter from '~/client/src/shared/components/DrillDownFilter'
import FilterResetter from '~/client/src/shared/components/FilterResetter/FilterResetter'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import { FilterButton } from '~/client/src/shared/components/Filters/triggers'
import { BallInCourtIcon } from '~/client/src/shared/components/icons/BallInCourtIcon'
import { UnreadIcon } from '~/client/src/shared/components/icons/UnreadIcon'
import { Toggle } from '~/client/src/shared/components/shadcn-ui/toggle'
import { patchQuery, setSearch } from '~/client/src/shared/lists/core'
import { buildNotificationsListFilterCategories } from '~/client/src/shared/lists/notifications/notificationListFilterCategories'
import {
  NotificationsListStore,
  NotificationTab,
} from '~/client/src/shared/lists/notifications/NotificationsList.store'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

import NotificationSavedViews from './NotificationSavedViews'

interface IProps {
  notificationsListStore: NotificationsListStore
  state?: DesktopInitialState
  companiesStore?: CompaniesStore
  permitTypesStore?: WorkflowTypesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
}

@inject(
  'state',
  'companiesStore',
  'permitTypesStore',
  'projectMembersStore',
  'locationAttributesStore',
)
@observer
export default class NotificationHeaderBar extends React.Component<IProps> {
  @observable private localProps: IProps = null
  @observable private showFilter: boolean = false
  private savedViewsRef = React.createRef<SavedViewsRef>()

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public render() {
    const { state } = this.props

    return (
      <div className="tw:flex tw:flex-col tw:shrink-0">
        {/* Row 1: Saved Views (includes preset tabs: All, Important, Updates, Archive) */}
        <div className="tw:border-b tw:border-border">
          <NotificationSavedViews
            ref={this.savedViewsRef}
            notificationsListStore={this.props.notificationsListStore}
            projectId={state.activeProject?.id}
            userId={state.user?.id}
            categories={this.drillDownCategories}
            onPresetTabSelect={this.handlePresetTabSelect}
          />
        </div>

        {/* Row 2: Filter Bar */}
        <div className="tw:flex tw:justify-start tw:items-center tw:border-b tw:border-border tw:px-2.5 tw:py-[5px] tw:gap-2">
          {this.renderSearchFilter()}
          <div className="tw:shrink-0">{this.renderFilterButton()}</div>
          <div className="tw:shrink-0">{this.renderBICButton()}</div>
          <div className="tw:shrink-0">{this.renderUnreadButton()}</div>
          <FilterResetter onReset={this.handleResetAll} />
        </div>
      </div>
    )
  }

  private renderSearchFilter() {
    const { notificationsListStore } = this.props

    return (
      <div className="tw:shrink-0">
        <SearchInput
          value={notificationsListStore.list.query.search || ''}
          onChange={this.handleSearchChange}
        />
      </div>
    )
  }

  private renderFilterButton() {
    const activeFilterIds = this.activeFilterIds

    return (
      <DrillDownFilter.Popover
        open={this.showFilter}
        onOpenChange={this.setShowFilter}
        side="bottom"
        align="start"
        trigger={
          <FilterButton
            label={Localization.translator.filter}
            onClick={this.toggleFilter}
            badgeCount={activeFilterIds.length}
          />
        }
      >
        <DrillDownFilter.Desktop
          categories={this.drillDownCategories}
          initialSelection={[...activeFilterIds]}
          onApply={this.handleFilterApply}
          onReset={this.handleFilterReset}
        />
      </DrillDownFilter.Popover>
    )
  }

  private renderBICButton() {
    const { notificationsListStore } = this.props

    return (
      <Toggle
        variant="outline"
        pressed={notificationsListStore.isBicFilterActive}
        onPressedChange={notificationsListStore.toggleBicOnly}
      >
        <BallInCourtIcon size="xs" />
        BIC
      </Toggle>
    )
  }

  private renderUnreadButton() {
    const { notificationsListStore } = this.props

    return (
      <Toggle
        variant="outline"
        pressed={notificationsListStore.isUnreadFilterActive}
        onPressedChange={notificationsListStore.toggleUnreadOnly}
      >
        <UnreadIcon size="sm" />
        {Localization.translator.unread}
      </Toggle>
    )
  }

  // --- DrillDownFilter Categories ---

  @computed
  private get drillDownCategories() {
    return buildNotificationsListFilterCategories({
      listStore: this.localProps.notificationsListStore,
      companiesStore: this.localProps.companiesStore,
      permitTypesStore: this.localProps.permitTypesStore,
      projectMembersStore: this.localProps.projectMembersStore,
      locationAttributesStore: this.localProps.locationAttributesStore,
    })
  }

  @computed
  private get activeFilterIds(): readonly string[] {
    return this.localProps.notificationsListStore.prefixedFilterIds
  }

  // --- Actions ---

  @action.bound
  private toggleFilter() {
    this.showFilter = !this.showFilter
  }

  @action.bound
  private setShowFilter(open: boolean) {
    this.showFilter = open
  }

  @action.bound
  private handleFilterApply(selectedIds: string[]) {
    this.localProps.notificationsListStore.applyPrefixedFilterIds(selectedIds)
  }

  @action.bound
  private handleFilterReset() {
    this.savedViewsRef.current?.selectView(NotificationTab.All)
  }

  @action.bound
  private handleResetAll() {
    this.props.state.notificationFilters.searchKey = ''
    this.localProps.notificationsListStore.list.dispatch(
      patchQuery({
        search: '',
      }),
    )
    this.localProps.notificationsListStore.setTab(NotificationTab.All)
    this.savedViewsRef.current?.selectView(NotificationTab.All)
  }

  @action.bound
  private handlePresetTabSelect(tab: NotificationTab) {
    this.props.notificationsListStore.setTab(tab)
    this.props.state.notificationFilters.searchKey =
      this.props.notificationsListStore.list.query.search
  }

  @action.bound
  private handleSearchChange(searchKey: string) {
    this.props.state.notificationFilters.searchKey = searchKey
    this.props.notificationsListStore.list.dispatch(setSearch(searchKey))
  }
}
